import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useCreatePracticeQuestion } from '@/api/useAdminPracticeQuestions';
import { useAdminQuestionCategories } from '@/api/useAdminQuestionCategories';
import SelectInput from '@/components/functional/SelectInput/SelectInput';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import Routes from '@/router/Routes';
import { Database } from '@/_constants/database.types';

type CategoryCode = Database['public']['Enums']['category_code'];

interface FormValues {
  category_code: CategoryCode | '';
  content: string;
  marks: number | '';
}

interface CreatePracticeQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreatePracticeQuestionModal: FC<CreatePracticeQuestionModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { mutate: createQuestion, isPending } = useCreatePracticeQuestion();
  const { data: categories = [], isLoading: categoriesLoading } = useAdminQuestionCategories();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      category_code: '',
      content: '',
      marks: '',
    },
    mode: 'onChange',
  });

  const categoryCode = watch('category_code');
  const content = watch('content');

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: FormValues) => {
    if (!data.category_code || !data.content.trim() || !data.marks) return;

    createQuestion(
      {
        category_code: data.category_code as CategoryCode,
        content: data.content.trim(),
        marks: Number(data.marks),
      },
      {
        onSuccess: (question) => {
          handleClose();
          navigate(Routes.ADMIN_PRACTICE_QUESTIONS + '/' + question.id);
        },
      }
    );
  };

  // Build category options from database
  const categoryOptions = [
    { value: '', label: categoriesLoading ? 'Loading...' : 'Select a category...' },
    ...categories.map((cat) => ({
      value: cat.category_code,
      label: `${cat.category_code} (${cat.name})`,
    })),
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={isPending ? undefined : handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6">
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={isPending}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Create Practice Question</h2>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Category Code */}
          <div>
            <SelectInput
              value={categoryCode}
              onChange={(value) => setValue('category_code', value as CategoryCode, { shouldValidate: true })}
              options={categoryOptions}
              label="Category Code *"
              disabled={categoriesLoading}
            />
            {errors.category_code && (
              <p className="text-sm text-red-600 mt-1">{errors.category_code.message}</p>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Question Content *</label>
            <textarea
              {...register('content', {
                required: 'Content is required',
                minLength: { value: 10, message: 'Content must be at least 10 characters' },
              })}
              placeholder="Enter the question content..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C87941] focus:border-transparent resize-none"
            />
            <div className="flex justify-between mt-1">
              {errors.content ? (
                <p className="text-sm text-red-600">{errors.content.message}</p>
              ) : (
                <span />
              )}
              <p className="text-xs text-gray-500">{content?.length || 0} characters</p>
            </div>
          </div>

          {/* Marks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Marks *</label>
            <input
              type="number"
              {...register('marks', {
                required: 'Marks is required',
                min: { value: 1, message: 'Marks must be at least 1' },
                max: { value: 40, message: 'Marks cannot exceed 40' },
              })}
              placeholder="Enter marks (1-40)"
              min={1}
              max={40}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C87941] focus:border-transparent"
            />
            {errors.marks && <p className="text-sm text-red-600 mt-1">{errors.marks.message}</p>}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              className="flex-1 px-4 py-3 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <LoadingButton
              type="submit"
              loading={isPending}
              disabled={!isValid || !categoryCode}
              className="flex-1 px-4 py-3 bg-[#0f172a] text-white font-semibold rounded-xl hover:bg-[#1e293b] transition-colors disabled:opacity-50"
            >
              Create & Edit
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePracticeQuestionModal;
